import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import {
  AverageActionsType,
  calculateSecurityCheckFamilyActionsCostsType,
  ExistingControlsbylabelType,
  GetSecurityCheckFamilyBySourceResponse,
  globalMaturityRateISOType,
  globalMaturityRateNISTType,
  globalMaturityRateType,
  IntersectionByPerimetreForSecurityControlType,
  LateRateForSecurityControlType,
  MaturityRateType,
  RisksAndIncidentsByExistingControlType,
  RisksAndIncidentsBySecurityControlType,
  SecurityCheckFamily,
  SecurityCheckFamilyResponse,
  SecurityCheckFamilyResponseSCFS,
  SecurityCheckResponse,
  SecurityControlMatrixType,
  SecurityTree,
} from '../../types/securityCheckFamilysTypes'
import {
  addSecurityControlToAction,
  calculateAverageActionsForSecurityControl,
  calculateLateRateForSecurityControls,
  calculateSecurityCheckFamilyActionsCosts,
  fetchSecurityCheckFamilyByNIS2Source,
  fetchSecurityCheckFamilyBySAMASource,
  fetchSecurityCheckFamilyBySWIFTSource,
  fetchSecurityCheckFamilyBySOCSource,
  getAllSecurity,
  getExistingControlsByLabel,
  getGlobalMaturityRate,
  getGlobalMaturityRateISO,
  getGlobalMaturityRateNIST,
  getIntersectionByPerimetreForSecurityControl,
  getMaturityRateForExistingControls,
  getRisksAndIncidentsByExistingControl,
  getRisksAndIncidentsBySecurityControl,
  getSecurityCheckFamiliesWithScores,
  getSecurityCheckFamiliesWithScoresById,
  getSecurityControls,
  getSecurityISO,
  getSecurityNIST,
  getSecurityTreeByFamilyId,
  getSubDomainByActionId,
  securityControlMatrix,
} from './securityCheckFamilyAction'
import { SecurityControls } from '../../types/securityControls'
import { SubDomain } from '../../types/subDomainTypes'

interface SecurityCheckFamilyState {
  securityCheckFamilys: SecurityCheckFamily[]
  securityCheckFamilysWithScores: SecurityCheckFamily[]
  loading: boolean
  securityCheckFamily: SecurityCheckFamily | null
  error: string | null
  addedSecurityCheckFamily: SecurityCheckFamily | null
  GlobalMaturityRate: globalMaturityRateType | null
  GlobalMaturityRateISO: globalMaturityRateISOType | null
  GlobalMaturityRateNIST: globalMaturityRateNISTType | null
  updatedSecurityCheckFamily: SecurityCheckFamily | null
  SecurityCheckFamilyResponse: SecurityCheckFamilyResponse | null
  SecurityControls: SecurityControls[]
  securityControl: SecurityControls | null
  subDomainsByActionId: SubDomain[]
  SecurityCheckFamilyActionsCosts: calculateSecurityCheckFamilyActionsCostsType | null
  SecurityTreeByFamilyId: SecurityTree | null
  securityControlM: SecurityControlMatrixType | null
  RisksAndIncidentsBySecurityControl: RisksAndIncidentsBySecurityControlType | null
  actionByPerimetreForSecurityControl: IntersectionByPerimetreForSecurityControlType | null
  AverageActionsForSecurityControl: AverageActionsType | null

  LateRateForSecurityControls: LateRateForSecurityControlType | null

  RisksAndIncidentsByExistingControl: RisksAndIncidentsByExistingControlType | null

  ExistingControlsByLabel: ExistingControlsbylabelType | null

  MaturityRateForExistingControls: MaturityRateType | null
  data: SecurityCheckResponse['data']
  dataSCFScore: SecurityCheckFamilyResponseSCFS | null
  dataSwift: SecurityCheckFamily[]
  currentPage: number
  totalPages: number
  dataSource: GetSecurityCheckFamilyBySourceResponse | null
}

const initialState: SecurityCheckFamilyState = {
  dataSource: null,
  dataSwift: [],
  currentPage: 1,
  totalPages: 0,
  securityCheckFamilys: [],
  securityCheckFamilysWithScores: [],
  data: [],
  loading: false,
  error: null,
  addedSecurityCheckFamily: null,
  securityCheckFamily: null,
  updatedSecurityCheckFamily: null,
  SecurityCheckFamilyResponse: null,
  SecurityControls: [],
  subDomainsByActionId: [],
  securityControl: null,
  GlobalMaturityRate: null,
  GlobalMaturityRateISO: null,
  GlobalMaturityRateNIST: null,

  actionByPerimetreForSecurityControl: null,
  AverageActionsForSecurityControl: null,

  LateRateForSecurityControls: null,

  RisksAndIncidentsByExistingControl: null,

  ExistingControlsByLabel: null,

  MaturityRateForExistingControls: null,
  SecurityCheckFamilyActionsCosts: null,
  SecurityTreeByFamilyId: null,
  securityControlM: null,
  RisksAndIncidentsBySecurityControl: null,
  dataSCFScore: null,
}

export const securityCheckFamilySlice = createSlice({
  name: 'securityCheckFamily',
  initialState,
  reducers: {
    resetSecurityCheckFamily: (state) => {
      state.SecurityCheckFamilyResponse = null
      state.SecurityControls = []
      state.loading = true
      state.error = null
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getSecurityISO.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(getSecurityISO.fulfilled, (state, action) => {
        state.loading = false
        state.SecurityCheckFamilyResponse = {
          securityCheckFamilies: action.payload.securityCheckFamilies,
          totalPages: action.payload.totalPages ?? 0,
          total: action.payload.total ?? 0,
        }
      })
      .addCase(getSecurityISO.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Something went wrong'
      })
      .addCase(fetchSecurityCheckFamilyBySWIFTSource.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(
        fetchSecurityCheckFamilyBySWIFTSource.fulfilled,
        (state, action) => {
          state.loading = false
          state.dataSwift = action.payload.securityCheckFamilies
          state.currentPage = action.payload.currentPage
          state.totalPages = action.payload.totalPages
          state.SecurityCheckFamilyResponse = {
            securityCheckFamilies: action.payload.securityCheckFamilies,
            totalPages: action.payload.totalPages ?? 0,
            total: action.payload.total ?? 0,
          }
        }
      )
      .addCase(
        fetchSecurityCheckFamilyBySWIFTSource.rejected,
        (state, action) => {
          state.loading = false
          state.error = action.payload ?? 'An error occurred'
        }
      )
      .addCase(getSecurityNIST.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(getSecurityNIST.fulfilled, (state, action) => {
        state.loading = false
        state.SecurityCheckFamilyResponse = {
          securityCheckFamilies: action.payload.securityCheckFamilies,
          totalPages: action.payload.totalPages ?? 0,
          total: action.payload.total ?? 0,
        }
      })
      .addCase(getSecurityNIST.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Something went wrong'
      })

      .addCase(getSubDomainByActionId.pending, (state) => {
        state.loading = true
        state.error = null
      })

      .addCase(getSubDomainByActionId.fulfilled, (state, action) => {
        state.loading = false
        const subDomains = Object.values(action.payload).filter(
          (item) => item && typeof item === 'object'
        )

        state.subDomainsByActionId = subDomains as []
      })
      .addCase(getSubDomainByActionId.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Something went wrong'
      })
      .addCase(addSecurityControlToAction.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Something went wrong'
      })

      .addCase(addSecurityControlToAction.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(addSecurityControlToAction.fulfilled, (state, action) => {
        state.loading = false
        state.securityControl = action.payload
      })
      .addCase(getAllSecurity.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(getAllSecurity.fulfilled, (state, action) => {
        state.loading = false
        state.SecurityCheckFamilyResponse = {
          securityCheckFamilies: action.payload.securityCheckFamilies,
          totalPages: action.payload.totalPages ?? 0,
          total: action.payload.total ?? 0,
        }
      })
      .addCase(getAllSecurity.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Something went wrong'
      })
      .addCase(getSecurityControls.pending, (state) => {
        state.loading = true
        state.error = null
        state.SecurityControls = []
      })
      .addCase(getSecurityControls.fulfilled, (state, action) => {
        state.loading = false
        state.SecurityControls = action.payload
      })
      .addCase(getSecurityControls.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Something went wrong'
      })

      .addCase(getGlobalMaturityRate.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(getGlobalMaturityRate.fulfilled, (state, action) => {
        state.loading = false
        state.GlobalMaturityRate = {
          ...state.GlobalMaturityRate,
          globalMaturityRate: action.payload.globalMaturityRate,
        }
      })
      .addCase(getGlobalMaturityRate.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Something went wrong'
      })
      .addCase(getGlobalMaturityRateISO.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(getGlobalMaturityRateISO.fulfilled, (state, action) => {
        state.loading = false
        state.GlobalMaturityRateISO = {
          ...state.GlobalMaturityRateISO,
          globalMaturityISO: action.payload.globalMaturityISO,
        }
      })
      .addCase(getGlobalMaturityRateISO.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Something went wrong'
      })
      .addCase(getGlobalMaturityRateNIST.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(getGlobalMaturityRateNIST.fulfilled, (state, action) => {
        state.loading = false
        state.GlobalMaturityRateNIST = {
          ...state.GlobalMaturityRateNIST,
          globalMaturityNIST: action.payload.globalMaturityNIST,
        }
      })
      .addCase(getGlobalMaturityRateNIST.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Something went wrong'
      })
      .addCase(calculateSecurityCheckFamilyActionsCosts.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(
        calculateSecurityCheckFamilyActionsCosts.fulfilled,
        (state, action) => {
          state.loading = false
          state.SecurityCheckFamilyActionsCosts = {
            totalCost: action.payload.totalCost,
          }
        }
      )
      .addCase(
        calculateSecurityCheckFamilyActionsCosts.rejected,
        (state, action) => {
          state.loading = false
          state.error = action.error.message || 'Something went wrong'
        }
      )

      .addCase(getSecurityTreeByFamilyId.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(getSecurityTreeByFamilyId.fulfilled, (state, action) => {
        state.loading = false
        state.SecurityTreeByFamilyId = action.payload
      })
      .addCase(getSecurityTreeByFamilyId.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Something went wrong'
      })

      .addCase(securityControlMatrix.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(securityControlMatrix.fulfilled, (state, action) => {
        state.loading = false
        state.securityControlM = action.payload
      })
      .addCase(securityControlMatrix.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Something went wrong'
      })

      .addCase(getRisksAndIncidentsBySecurityControl.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(
        getRisksAndIncidentsBySecurityControl.fulfilled,
        (state, action) => {
          state.loading = false
          state.RisksAndIncidentsBySecurityControl = action.payload
        }
      )
      .addCase(
        getRisksAndIncidentsBySecurityControl.rejected,
        (state, action) => {
          state.loading = false
          state.error = action.error.message || 'Something went wrong'
        }
      )

      .addCase(
        getIntersectionByPerimetreForSecurityControl.pending,
        (state) => {
          state.loading = true
          state.error = null
        }
      )
      .addCase(
        getIntersectionByPerimetreForSecurityControl.fulfilled,
        (state, action) => {
          state.loading = false
          state.actionByPerimetreForSecurityControl = action.payload
        }
      )
      .addCase(
        getIntersectionByPerimetreForSecurityControl.rejected,
        (state, action) => {
          state.loading = false
          state.error = action.error.message || 'Something went wrong'
        }
      )

      .addCase(calculateAverageActionsForSecurityControl.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(
        calculateAverageActionsForSecurityControl.fulfilled,
        (state, action) => {
          state.loading = false
          state.AverageActionsForSecurityControl = action.payload
        }
      )
      .addCase(
        calculateAverageActionsForSecurityControl.rejected,
        (state, action) => {
          state.loading = false
          state.error = action.error.message || 'Something went wrong'
        }
      )

      .addCase(calculateLateRateForSecurityControls.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(
        calculateLateRateForSecurityControls.fulfilled,
        (state, action) => {
          state.loading = false
          state.LateRateForSecurityControls = action.payload
        }
      )
      .addCase(
        calculateLateRateForSecurityControls.rejected,
        (state, action) => {
          state.loading = false
          state.error = action.error.message || 'Something went wrong'
        }
      )

      .addCase(getRisksAndIncidentsByExistingControl.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(
        getRisksAndIncidentsByExistingControl.fulfilled,
        (state, action) => {
          state.loading = false
          state.RisksAndIncidentsByExistingControl = action.payload
        }
      )
      .addCase(
        getRisksAndIncidentsByExistingControl.rejected,
        (state, action) => {
          state.loading = false
          state.error = action.error.message || 'Something went wrong'
        }
      )

      .addCase(getExistingControlsByLabel.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(getExistingControlsByLabel.fulfilled, (state, action) => {
        state.loading = false
        state.ExistingControlsByLabel = action.payload
      })
      .addCase(getExistingControlsByLabel.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Something went wrong'
      })

      .addCase(getMaturityRateForExistingControls.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(
        getMaturityRateForExistingControls.fulfilled,
        (state, action) => {
          state.loading = false
          state.MaturityRateForExistingControls = action.payload
        }
      )
      .addCase(getMaturityRateForExistingControls.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Something went wrong'
      })

      .addCase(getSecurityCheckFamiliesWithScores.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(
        getSecurityCheckFamiliesWithScores.fulfilled,
        (state, action: PayloadAction<SecurityCheckResponse>) => {
          state.loading = false
          state.data = action.payload.data
        }
      )
      .addCase(getSecurityCheckFamiliesWithScores.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload || 'An error occurred'
      })
      .addCase(getSecurityCheckFamiliesWithScoresById.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(
        getSecurityCheckFamiliesWithScoresById.fulfilled,
        (state, action: PayloadAction<SecurityCheckFamilyResponseSCFS>) => {
          state.loading = false
          state.dataSCFScore = action.payload
        }
      )
      .addCase(
        getSecurityCheckFamiliesWithScoresById.rejected,
        (state, action) => {
          state.loading = false
          state.error = action.payload || 'An error occurred'
        }
      )
      .addCase(fetchSecurityCheckFamilyBySAMASource.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(
        fetchSecurityCheckFamilyBySAMASource.fulfilled,
        (state, action) => {
          state.loading = false
          state.dataSource = action.payload
          state.SecurityCheckFamilyResponse = {
            securityCheckFamilies: action.payload.securityCheckFamilies,
            totalPages: action.payload.totalPages ?? 0,
            total: action.payload.total ?? 0,
          }
        }
      )
      .addCase(
        fetchSecurityCheckFamilyBySAMASource.rejected,
        (state, action) => {
          state.loading = false
          state.error = action.payload ?? 'An error occurred'
        }
      )
      .addCase(fetchSecurityCheckFamilyByNIS2Source.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(
        fetchSecurityCheckFamilyByNIS2Source.fulfilled,
        (state, action) => {
          state.loading = false
          state.dataSource = action.payload
          state.SecurityCheckFamilyResponse = {
            securityCheckFamilies: action.payload.securityCheckFamilies,
            totalPages: action.payload.totalPages ?? 0,
            total: action.payload.total ?? 0,
          }
        }
      )
      .addCase(
        fetchSecurityCheckFamilyByNIS2Source.rejected,
        (state, action) => {
          state.loading = false
          state.error = action.payload ?? 'An error occurred'
        }
      )
      .addCase(fetchSecurityCheckFamilyBySOCSource.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchSecurityCheckFamilyBySOCSource.fulfilled, (state, action) => {
        state.loading = false
        state.SecurityCheckFamilyResponse = {
          securityCheckFamilies: action.payload.securityCheckFamilies,
          totalPages: action.payload.totalPages ?? 0,
          total: action.payload.total ?? 0,
        }
      })
      .addCase(fetchSecurityCheckFamilyBySOCSource.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload ?? 'An error occurred'
      })
  },
})
export const { resetSecurityCheckFamily } = securityCheckFamilySlice.actions
export default securityCheckFamilySlice.reducer
